<script setup lang="ts">
/**
 * CampaignMobileStats — bandeau compact mobile : ring de progression +
 * montant collecté (ou nombre de participants) + dernier donateur.
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps.
 *
 * `<UIcon>` reste global (résolu par l'app Nuxt qui transpile ce SFC via
 * build.transpile).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignMobileStats from "@donoor/ui/campaign/CampaignMobileStats.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
import type { Campaign, CampaignDonor } from "./types";
import { useAnimatedNumber } from "@donoor/composables";

// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`,
// présentes dans donoor.org ET dashboard.donoor.org (useI18n auto-importé).
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    campaign: Campaign;
    progressPercent: number;
    hasGoal: boolean;
    formatAmountShort: (amount: number) => string;
    formatAmountCompact?: (amount: number | null | undefined) => string;
    getRelativeTime: (dateString: string) => string;
    showAmountRaised?: boolean;
    donors: CampaignDonor[];
  }>(),
  {
    showAmountRaised: true,
    formatAmountCompact: undefined,
  }
);

const { displayValue: animatedAmount, setValue: setAmount } = useAnimatedNumber(0, 1400);
const { displayValue: animatedProgress, setValue: setProgress } = useAnimatedNumber(0, 1400);

watch(
  () => props.campaign.raised,
  (newVal) => {
    setAmount(newVal || 0);
  }
);

watch(
  () => props.progressPercent,
  (newVal) => {
    setProgress(newVal || 0);
  }
);

onMounted(() => {
  setAmount(props.campaign.raised || 0);
  setProgress(props.progressPercent || 0);
});

// Progress ring
const RING_SIZE = 64;
const RING_STROKE = 6;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const ringDashOffset = computed(
  () =>
    RING_CIRCUMFERENCE - (animatedProgress.value / 100) * RING_CIRCUMFERENCE
);

const latestDonor = computed(() => props.donors[0] || null);

function defaultAmountCompact(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(".0", "")}M €`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1).replace(".0", "")}k €`;
  }
  return `${Math.round(n)} €`;
}
const amountCompact = (a: number | null | undefined) =>
  props.formatAmountCompact ? props.formatAmountCompact(a) : defaultAmountCompact(a);
</script>

<template>
  <div class="flex items-center gap-3">
    <!-- Ring de progression -->
    <div v-if="hasGoal" class="relative shrink-0">
      <svg
        :width="RING_SIZE"
        :height="RING_SIZE"
        class="-rotate-90"
      >
        <defs>
          <linearGradient id="mobile-progress-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#1f9afe" />
            <stop offset="100%" stop-color="#1366B5" />
          </linearGradient>
        </defs>
        <circle
          :cx="RING_SIZE / 2"
          :cy="RING_SIZE / 2"
          :r="RING_RADIUS"
          stroke="#e5e7eb"
          :stroke-width="RING_STROKE"
          fill="none"
        />
        <circle
          :cx="RING_SIZE / 2"
          :cy="RING_SIZE / 2"
          :r="RING_RADIUS"
          stroke="url(#mobile-progress-gradient)"
          :stroke-width="RING_STROKE"
          fill="none"
          stroke-linecap="round"
          :stroke-dasharray="RING_CIRCUMFERENCE"
          :stroke-dashoffset="ringDashOffset"
          class="transition-all duration-700"
        />
      </svg>
      <span class="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
        {{ Math.round(animatedProgress) }}%
      </span>
    </div>

    <!-- Montant + dernier donateur -->
    <div v-if="showAmountRaised" class="min-w-0 flex-1">
      <p class="text-xl xs:text-2xl font-extrabold text-gray-900 leading-tight text-balance">
        <!-- Montant collecté : compact sous xs (184 447 € → 184 k €) pour ne pas
             déborder sur petits écrans, valeur pleine dès xs (≥480px). -->
        <span class="whitespace-nowrap xs:hidden">{{ amountCompact(animatedAmount) }}</span>
        <span class="whitespace-nowrap hidden xs:inline">{{ formatAmountShort(animatedAmount) }}</span>
        <span class="text-gray-500 font-semibold text-sm xs:text-base ml-1">{{ t("ui.commun.collectes") }}</span>
        <template v-if="hasGoal">
          <span class="text-gray-500 font-semibold text-sm xs:text-base ml-1">{{ t("ui.commun.sur") }}</span>
          <span class="ml-1 whitespace-nowrap">{{ amountCompact(campaign.goal_amount) }}</span>
        </template>
      </p>
      <p
        v-if="latestDonor"
        class="text-sm text-gray-500 truncate mt-1"
      >
        <span class="font-semibold text-gray-700">
          {{ latestDonor.is_anonymous ? t("ui.commun.anonyme") : latestDonor.name }}
        </span>
        {{ t("ui.commun.aDonne") }}
        <span class="font-semibold text-gray-700">{{ formatAmountShort(latestDonor.amount) }}</span>
        <UIcon name="i-lucide-chevron-right" class="size-3 inline-block align-middle ml-0.5" />
      </p>
      <p v-else class="text-xs text-gray-500">
        {{ t("ui.mobileStats.soyezLePremier") }}
      </p>
    </div>

    <!-- Cas sans objectif -->
    <div v-else class="min-w-0 flex-1">
      <p class="text-2xl font-extrabold text-gray-900 leading-tight">
        {{ campaign.donors_count || 0 }}
        <span class="text-gray-500 font-semibold text-base ml-1">{{ t("ui.commun.participant", campaign.donors_count || 0) }}</span>
      </p>
    </div>
  </div>
</template>
